import React, { useEffect, useMemo, useRef, useState } from "react";

import { CONTAINER_MAX_WIDTH } from "../../../styles/layout";
import {
  AdminCodeGive,
  AdminMissingReports,
  AdminSessionTable,
} from "./LevelTestAdminPanel";
import LevelTestCodeVault from "./LevelTestCodeVault";
import { showGrade } from "../../../utils/textUtils";
import {
  buildLevelTestReportBySessionAPI,
  fetchCodeVaultAPI,
  fetchLevelTestReportStatusBySessionAPI,
  fetchTeacherLevelTestAPI,
  saveLevelTestInviteAPI,
  sendEnrolledNoticeAPI,
  sendLevelTestInviteAPI,
  fetchAcademyReviewsAPI,
  publishAcademyReviewAPI,
} from "../levelTestAPI";
import "./LevelTestManagement.css";

// 레벨테스트 관리. 세 갈래를 한 화면에서 오간다.
//   재원생    우리 학생과 이어진 응시 기록. 월별 점수 추적표
//   신규생    지인에듀 신청서 + 응시 기록. 응시코드와 결과
//   단어리스트 학생이 신청한 모르는 단어. 제목까지 한 번에 복사한다
//
// 재원생 전환이 신규생 -> 재원생으로 넘어가는 동작이라 세 탭을 한 화면에 뒀다.
// 밖으로 내보내는 주소. 탭마다 하나씩이고, 탭 줄 오른쪽 끝의 버튼이 그것을 복사한다.
//
// **응시링크만은 서버가 주는 값(`data.test_player_url`)을 쓴다.** 여기 박아 두었던
// 지인에듀 원본(`www.ginedu.com/teacher/test-player.html`)은 아직 GS 를 보기 때문에
// 우리가 발급한 응시코드를 '없는 코드' 로 거절하고, 화면도 옛것이 뜬다. 선생님이
// 그 링크를 학부모에게 보내면 아이가 응시를 못 한다.
//
// 아래 값은 서버 응답이 없을 때만 쓰는 마지막 대비책이다.
const TEST_PLAYER_URL = "https://teacher.submitmyhomework.kr/teacher/test-player.html";
const APPLY_URL = "https://www.ginedu.com/teacher/apply.html";
// 학생이 모르는 단어를 직접 적어 보내는 곳. 여기로 들어온 것이 단어리스트 탭에 쌓인다.
const WORD_TRIAL_URL = "https://www.ginedu.com/teacher/word-free-trial.html";

// 탭 하나에 링크 하나. 안내 문구를 따로 두지 않고 버튼 이름이 그 역할을 한다.
// 운영진에게만 여는 탭. 학원 계정에는 아예 안 보인다(차단은 서버가 한다).
const ADMIN_TAB_LIST = [
  { key: "all", text: "전체 응시" },
  { key: "give", text: "코드 발급" },
  { key: "missing", text: "미생성 리포트" },
];

const TAB_LIST = [
  { key: "enrolled", text: "재원생", linkText: "응시링크", linkUrl: TEST_PLAYER_URL },
  { key: "new", text: "신규생", linkText: "신청링크", linkUrl: APPLY_URL },
  {
    key: "words",
    text: "단어리스트",
    linkText: "단어 제출 링크",
    linkUrl: WORD_TRIAL_URL,
  },
  // 리포트에서 학부모·학생이 쓴 학원 후기. '홈페이지 전송' 을 누르면 지인에듀
  // 성적향상사례에 실린다 (공개 여부는 서버 is_published).
  { key: "reviews", text: "후기" },
];

// "2026-08" -> "8월"
const formatMonthLabel = (month) =>
  month ? `${Number(month.slice(5, 7))}월` : "";

// 신규생 등록 초안의 빈 값. 입력칸이 제어 컴포넌트라 undefined 가 들어가면 안 된다.
//
// 자동 발송 스위치는 여기에 없다. 알림톡 관리의 발송조건('신규생 응시 안내')이
// 학원 단위로 정한다. 등록마다 따로 끄게 하면 어디까지 나갔는지 두 군데를 봐야 한다.
const EMPTY_INVITE = {
  name: "",
  parent_phone: "",
  school: "",
  grade_text: "",
};

// 리포트가 다 됐는지 물어보는 간격과 횟수. 굽는 데 1~3분이라 5초씩 5분까지 본다.
// 그 안에 안 끝나도 서버는 계속 굽고 있으므로, 화면을 다시 열면 리포트가 붙어 있다.
const REPORT_POLL_MS = 5000;
const REPORT_POLL_MAX = 60;

const copyToClipboard = (text, onDone) => {
  const fallback = () => {
    const area = document.createElement("textarea");
    area.value = text;
    area.style.position = "fixed";
    area.style.opacity = "0";
    document.body.appendChild(area);
    area.select();
    try {
      document.execCommand("copy");
    } catch (error) {
      // 복사를 막는 브라우저도 있다. 실패해도 화면은 그대로 둔다.
    }
    document.body.removeChild(area);
  };

  if (navigator.clipboard && navigator.clipboard.writeText) {
    navigator.clipboard.writeText(text).then(onDone, () => {
      fallback();
      onDone();
    });
    return;
  }

  fallback();
  onDone();
};

const CopyButton = ({ text, label = "복사", className = "level-test-copy" }) => {
  const [isDone, setIsDone] = useState(false);

  return (
    <button
      type="button"
      className={className}
      onClick={() =>
        copyToClipboard(text, () => {
          setIsDone(true);
          setTimeout(() => setIsDone(false), 1500);
        })
      }
    >
      {isDone ? "복사됨" : label}
    </button>
  );
};

// 리포트 칸. 있으면 링크, 없으면 그 자리에서 굽는 단추다.
//
// 아이가 시험을 끝내도 리포트는 저절로 생기지 않는다(서버가 답안까지만 저장한다).
// 원장이 눌러 오던 GS 관리자 화면의 생성 단추가 우리 DB 를 못 봐서 재원생·신규생
// 양쪽에 이 단추를 둔다.
const ReportCell = ({
  sessionId,
  reportUrl,
  isCompleted,
  state,
  onBuild,
  // 보여줄 것이 없을 때 남길 글. 월별 칸에서는 '진행중' 아래에 줄이 하나 더
  // 생기지 않게 빈 값을 준다.
  emptyText = "—",
}) => {
  const url = state?.report_url || reportUrl;

  if (url) {
    return (
      <a
        className="level-test-report"
        href={url}
        target="_blank"
        rel="noreferrer"
      >
        리포트
      </a>
    );
  }

  // 아직 안 끝낸 시험은 굽지 않는다. 푼 데까지로 만들면 점수가 실제보다 낮게 나간다.
  if (!sessionId || !isCompleted) {
    return emptyText ? <span className="is-muted">{emptyText}</span> : null;
  }

  if (state?.status === "building") {
    return (
      <span className="level-test-make level-test-make--busy">만드는 중…</span>
    );
  }

  const isFailed = state?.status === "failed";

  return (
    <button
      type="button"
      className={`level-test-make${isFailed ? " level-test-make--failed" : ""}`}
      onClick={() => onBuild(sessionId)}
      title={state?.error || ""}
    >
      {isFailed ? "다시 생성" : "리포트 생성"}
    </button>
  );
};

const LevelTestManagement = () => {
  const [tab, setTab] = useState("enrolled");
  // 학원 후기. 관리 화면에 들어올 때 한 번 받는다.
  const [reviewList, setReviewList] = useState([]);
  useEffect(() => {
    let isCurrent = true;
    fetchAcademyReviewsAPI()
      .then((res) => {
        if (isCurrent) setReviewList(res.reviews || []);
      })
      .catch(() => {});
    return () => {
      isCurrent = false;
    };
  }, []);

  const toggleReviewPublish = (review) => {
    const next = !review.is_published;
    publishAcademyReviewAPI(review.review_id, next)
      .then(() => {
        setReviewList((previous) =>
          previous.map((one) =>
            one.review_id === review.review_id
              ? { ...one, is_published: next }
              : one
          )
        );
      })
      .catch(() => {
        alert("홈페이지 전송에 실패했어요. 잠시 뒤 다시 눌러 주세요.");
      });
  };
  const [data, setData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [openWordRow, setOpenWordRow] = useState(null);
  // 신규생 등록. 저장한 결과를 여기에 들고 있어야 전체를 다시 안 불러도 화면이 바뀐다.
  const [inviteByCode, setInviteByCode] = useState({});
  const [openInviteCode, setOpenInviteCode] = useState(null);
  const [inviteDraft, setInviteDraft] = useState(EMPTY_INVITE);
  const [sendingCode, setSendingCode] = useState("");
  // 이번 달 응시 안내를 보낸 재원생. 목록을 다시 안 불러도 칸이 바뀌게 들고 있는다.
  const [noticedStudentIdSet, setNoticedStudentIdSet] = useState(() => new Set());
  const [noticingStudentId, setNoticingStudentId] = useState(null);
  // 운영진이 들여다보는 학원. 빈 값이면 자기 학원이다. 학원 계정에는 고를 것이 없다.
  const [viewingNickname, setViewingNickname] = useState("");
  // 굽고 있는(또는 방금 구운) 리포트. 세션ID -> {status, report_url, error}
  const [reportBySession, setReportBySession] = useState({});
  // 세션마다 물어보는 타이머 하나. 화면을 떠날 때 전부 걷어낸다.
  const reportTimerRef = useRef({});

  useEffect(() => {
    let isCurrent = true;
    setIsLoading(true);
    fetchTeacherLevelTestAPI(viewingNickname)
      .then((result) => {
        if (isCurrent) {
          setData(result);
          // 서버가 준 등록 정보를 초기값으로 깐다. 이후 저장·발송은 이 위에 얹힌다.
          setInviteByCode(
            Object.fromEntries(
              (result.codes || [])
                .filter((row) => row.invite)
                .map((row) => [row.code, row.invite])
            )
          );
          // 학원을 바꿔 보는 중이면 앞 학원에서 눌러 둔 흔적이 남으면 안 된다.
          setNoticedStudentIdSet(new Set());
          setOpenInviteCode(null);
          setOpenWordRow(null);
          setIsLoading(false);
        }
      })
      .catch(() => {
        if (isCurrent) {
          setIsLoading(false);
        }
      });

    return () => {
      isCurrent = false;
    };
  }, [viewingNickname]);

  // 계약(full) 학원이라도 계정 앞으로 배정된 응시코드(설문이벤트·개인판매)가
  // 있을 수 있다. 코드함은 원래 light 전용 화면이라 full 화면에서는 볼 곳이
  // 없었다(2026-08-25 이은미 학원). 코드가 있을 때만 탭을 더한다.
  // 주의: counts 가 이 값을 읽으므로 반드시 counts 선언보다 위에 있어야 한다.
  const [vaultCodeCount, setVaultCodeCount] = useState(0);
  useEffect(() => {
    let isCurrent = true;
    fetchCodeVaultAPI()
      .then((vault) => {
        // 응답 키는 usable(안 쓴 코드)·used(쓴 코드)다. 쓴 코드만 남았어도
        // 리포트를 보러 올 수 있으므로 둘 다 센다.
        if (isCurrent)
          setVaultCodeCount(
            (vault?.usable || []).length + (vault?.used || []).length
          );
      })
      .catch(() => {});
    return () => {
      isCurrent = false;
    };
  }, []);

  const counts = useMemo(
    () => ({
      enrolled: data?.enrolled?.length || 0,
      new: data?.new_students?.length || 0,
      words: data?.word_trials?.length || 0,
      reviews: reviewList.length,
      vault: vaultCodeCount,
    }),
    [data, vaultCodeCount]
  );

  const baseTabList = data?.is_admin ? [...TAB_LIST, ...ADMIN_TAB_LIST] : TAB_LIST;
  const tabList =
    vaultCodeCount > 0
      ? [...baseTabList, { key: "vault", text: "코드함" }]
      : baseTabList;
  const activeTab = tabList.find((item) => item.key === tab) || TAB_LIST[0];
  // 응시링크는 서버가 정본을 준다. 나머지 두 링크는 지인에듀 쪽 신청 화면이 맞다.
  const activeLinkUrl =
    activeTab.key === "enrolled"
      ? data?.test_player_url || TEST_PLAYER_URL
      : activeTab.linkUrl;

  useEffect(() => {
    const timerMap = reportTimerRef.current;
    return () => {
      Object.values(timerMap).forEach(clearTimeout);
    };
  }, []);

  // 다 됐는지 되물어본다. 굽는 것은 서버가 별도 프로세스로 하므로, 이 화면을 닫아도
  // 리포트는 만들어진다. 여기서 하는 일은 다 된 순간 칸을 바꿔 주는 것뿐이다.
  const pollReport = (sessionId, tryCount) => {
    reportTimerRef.current[sessionId] = setTimeout(() => {
      fetchLevelTestReportStatusBySessionAPI(sessionId)
        .then((result) => {
          setReportBySession((previous) => ({ ...previous, [sessionId]: result }));
          if (result.status === "building" && tryCount < REPORT_POLL_MAX) {
            pollReport(sessionId, tryCount + 1);
          }
        })
        .catch(() => {
          // 한 번 못 물어본 것으로 그만두지 않는다. 다음 차례에 다시 묻는다.
          if (tryCount < REPORT_POLL_MAX) {
            pollReport(sessionId, tryCount + 1);
          }
        });
    }, REPORT_POLL_MS);
  };

  const handleReportBuild = (sessionId) => {
    if (reportBySession[sessionId]?.status === "building") {
      return;
    }
    if (
      !window.confirm(
        "리포트를 만듭니다. 1~3분쯤 걸리고, 이 화면을 닫아도 계속 만들어집니다."
      )
    ) {
      return;
    }

    setReportBySession((previous) => ({
      ...previous,
      [sessionId]: { status: "building", report_url: "", error: "" },
    }));

    buildLevelTestReportBySessionAPI(sessionId)
      .then((result) => {
        setReportBySession((previous) => ({ ...previous, [sessionId]: result }));
        if (result.status === "building") {
          pollReport(sessionId, 0);
        }
      })
      .catch((error) => {
        const message =
          error?.response?.data?.error || "리포트 생성을 시작하지 못했습니다.";
        setReportBySession((previous) => ({
          ...previous,
          [sessionId]: { status: "failed", report_url: "", error: message },
        }));
        alert(message);
      });
  };

  const handleEnrolledNotice = (studentId) => {
    // disabled 로 막지 않는다. 두 번 눌리는 것만 여기서 막는다.
    if (noticingStudentId) {
      return;
    }
    if (!window.confirm("이번 달 응시 안내 알림톡을 보냅니다.")) {
      return;
    }

    setNoticingStudentId(studentId);
    sendEnrolledNoticeAPI(studentId)
      .then(() => {
        setNoticedStudentIdSet((previous) => new Set(previous).add(studentId));
      })
      .catch((error) => {
        alert(error?.response?.data?.error || "발송에 실패했습니다.");
      })
      .finally(() => setNoticingStudentId(null));
  };

  const openInviteEditor = (code, invite) => {
    if (openInviteCode === code) {
      setOpenInviteCode(null);
      return;
    }
    setInviteDraft(invite ? { ...EMPTY_INVITE, ...invite } : EMPTY_INVITE);
    setOpenInviteCode(code);
  };

  const handleInviteSave = (code) => {
    if (!inviteDraft.name.trim()) {
      alert("학생 이름을 적어주세요.");
      return;
    }

    saveLevelTestInviteAPI({ ...inviteDraft, code })
      .then((saved) => {
        setInviteByCode((previous) => ({ ...previous, [code]: saved }));
        setOpenInviteCode(null);
      })
      .catch((error) => {
        alert(error?.response?.data?.error || "등록에 실패했습니다.");
      });
  };

  const handleInviteSend = (code) => {
    const invite = inviteByCode[code];
    const name = invite ? invite.name : "";
    if (!window.confirm(`${name} 학부모에게 응시 안내 알림톡을 보냅니다.`)) {
      return;
    }

    setSendingCode(code);
    sendLevelTestInviteAPI(code)
      .then((result) => {
        setInviteByCode((previous) => ({
          ...previous,
          [code]: { ...previous[code], ...result },
        }));
      })
      .catch((error) => {
        const message = error?.response?.data?.error || "발송에 실패했습니다.";
        setInviteByCode((previous) => ({
          ...previous,
          [code]: { ...previous[code], send_status: "failed", send_error: message },
        }));
        alert(message);
      })
      .finally(() => setSendingCode(""));
  };

  // 학원을 바꿔 보는 중에는 앞 학원 화면을 그대로 두고 바뀐 것만 갈아끼운다.
  // 통째로 '불러오는 중' 으로 바꾸면 학원 고르는 칸까지 사라져 다시 고를 수가 없다.
  if (!data) {
    return (
      <div className="level-test-page">
        <p className="level-test-empty">
          {isLoading ? "불러오는 중…" : "레벨테스트 기록이 없습니다."}
        </p>
      </div>
    );
  }

  return (
    <div
      className="level-test-page"
      style={{ maxWidth: `${CONTAINER_MAX_WIDTH}px` }}
    >
      <h3 className="section-title level-test-page__title">
        레벨테스트 관리
        {/* 운영진에게만 학원 목록이 온다. 학원 계정에는 이 칸이 아예 안 생긴다. */}
        {(data.academy_list || []).length > 0 && (
          <select
            className="level-test-academy-select"
            value={viewingNickname}
            onChange={(event) => setViewingNickname(event.target.value)}
          >
            <option value="">내 학원</option>
            {data.academy_list.map((item) => (
              <option key={item.nickname} value={item.nickname}>
                {item.academy}
              </option>
            ))}
          </select>
        )}
        {data.is_readonly && (
          <span className="level-test-readonly">
            보기 전용 · {data.academy} 화면을 그대로 봅니다
          </span>
        )}
        {isLoading && <span className="level-test-readonly">불러오는 중…</span>}
        {/* 우리 계정에는 학원이 없다. 고르라고 알려 줘야 빈 화면을 오해하지 않는다. */}
        {!data.academy && (data.academy_list || []).length > 0 && (
          <span className="level-test-readonly">학원을 고르세요</span>
        )}
      </h3>

      {/* 탭과 링크 버튼을 한 줄에 둔다. 좁아지면 버튼이 아래로 내려간다. */}
      <div className="level-test-page__bar">
        <div className="level-test-page__tabs">
          {tabList.map((item) => (
            <button
              key={item.key}
              type="button"
              className={`level-test-tab${tab === item.key ? " level-test-tab--on" : ""}`}
              onClick={() => setTab(item.key)}
            >
              {item.text}
              {counts[item.key] !== undefined && (
                <span className="level-test-tab__count">{counts[item.key]}</span>
              )}
            </button>
          ))}
        </div>
        {/* 지금 탭의 링크만 내보낸다. 탭과 같은 크기라 한 줄로 읽힌다.
            운영진 탭에는 내보낼 링크가 없다. */}
        {activeTab.linkText && (
          <CopyButton
            className="level-test-link"
            text={activeLinkUrl}
            label={activeTab.linkText}
          />
        )}
      </div>

      {tab === "enrolled" && (
        <div className="level-test-card">
          <div className="level-test-scroll">
            <table className="level-test-table">
              <colgroup>
                <col style={{ width: "13%" }} />
                <col style={{ width: "7%" }} />
                <col style={{ width: "14%" }} />
                {data.months.map((month) => (
                  <col key={month} />
                ))}
                <col style={{ width: "16%" }} />
              </colgroup>
              <tbody>
                <tr className="level-test-table__head">
                  <td>학생</td>
                  <td>학년</td>
                  <td className="is-center">이달 응시코드</td>
                  {data.months.map((month) => (
                    <td key={month} className="is-center">
                      {formatMonthLabel(month)}
                    </td>
                  ))}
                  <td className="is-center">추출된 단어</td>
                </tr>
                {data.enrolled.map((row) => (
                  <tr key={row.student_id}>
                    <td>
                      {/* 이름을 누르면 그 학생 화면으로 바로 들어간다. */}
                      <a
                        className="level-test-student"
                        href={row.student_url}
                        target="_blank"
                        rel="noreferrer"
                      >
                        {row.name}
                      </a>
                    </td>
                    <td>{showGrade(row.grade)}</td>
                    <td className="is-center">
                      {row.month_code ? (
                        <>
                          <span className="level-test-code">{row.month_code}</span>
                          {/* 배정이 아니라 이미 쓴 코드다. 그 학생은 자기 몫을
                              이번 달에 이미 썼다. */}
                          {row.is_code_used && (
                            <div className="level-test-code-used">사용함</div>
                          )}
                        </>
                      ) : (
                        <span className="is-muted">미배정</span>
                      )}
                    </td>
                    {data.months.map((month) => {
                      const result = row.results_by_month[month];
                      if (!result) {
                        // 이번 달을 아직 안 봤으면 여기서 바로 안내를 보낸다.
                        const isCurrentMonth = month === data.current_month;
                        const isNoticed = noticedStudentIdSet.has(row.student_id);

                        return (
                          <td key={month} className="is-center">
                            {/* 남의 학원 학생에게 알림톡을 보낼 수는 없다. */}
                            {!isCurrentMonth || data.is_readonly ? (
                              <span className="is-muted">—</span>
                            ) : isNoticed ? (
                              <span className="level-test-badge">발송됨</span>
                            ) : (
                              <button
                                type="button"
                                className="level-test-send"
                                onClick={() => handleEnrolledNotice(row.student_id)}
                              >
                                {noticingStudentId === row.student_id
                                  ? "보내는 중…"
                                  : "알림톡 발송"}
                              </button>
                            )}
                          </td>
                        );
                      }
                      return (
                        <td key={month} className="is-center">
                          {result.score_percent === null ? (
                            <span className="level-test-progress">진행중</span>
                          ) : (
                            <b className="level-test-score">
                              {result.score_percent}%
                            </b>
                          )}
                          <div className="level-test-stage">
                            S{String(result.stage).padStart(2, "0")}
                          </div>
                          <ReportCell
                            sessionId={result.session_id}
                            reportUrl={result.report_url}
                            isCompleted={result.status === "completed"}
                            state={reportBySession[result.session_id]}
                            onBuild={handleReportBuild}
                            emptyText=""
                          />
                        </td>
                      );
                    })}
                    <td className="is-center">
                      {row.word_count > 0 ? (
                        <span className="level-test-words">
                          <span className="level-test-words__count">
                            {row.word_count}개
                          </span>
                          <CopyButton text={row.word_title} label="제목" />
                          <CopyButton text={row.words_text} label="단어" />
                        </span>
                      ) : (
                        <span className="is-muted">—</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {tab === "new" && (
        <>
          <div className="level-test-card">
            <div className="level-test-scroll">
              <table className="level-test-table">
                <colgroup>
                  <col style={{ width: "12%" }} />
                  <col style={{ width: "10%" }} />
                  <col style={{ width: "20%" }} />
                  <col style={{ width: "18%" }} />
                  <col style={{ width: "12%" }} />
                  <col style={{ width: "14%" }} />
                  <col style={{ width: "14%" }} />
                </colgroup>
                <tbody>
                  <tr className="level-test-table__head">
                    <td>이름</td>
                    <td>학년</td>
                    <td>학교</td>
                    <td>학부모 번호</td>
                    <td className="is-center">응시</td>
                    <td className="is-center">점수</td>
                    <td className="is-center">리포트</td>
                  </tr>
                  {data.new_students.map((row) => (
                    <tr key={`${row.name}-${row.grade_text}`}>
                      <td className="is-strong">{row.name}</td>
                      <td>{row.grade_text}</td>
                      <td>{row.school || "—"}</td>
                      <td>
                        {row.parent_phone ? (
                          <span className="level-test-phone">
                            {row.parent_phone}
                            <CopyButton text={row.parent_phone} />
                          </span>
                        ) : (
                          "—"
                        )}
                      </td>
                      <td className="is-center">
                        {row.test_count > 0 ? (
                          <span className="level-test-badge">{row.test_count}회</span>
                        ) : (
                          <span className="is-muted">미응시</span>
                        )}
                      </td>
                      <td className="is-center">
                        {row.score_percent === null ? (
                          <span className="is-muted">—</span>
                        ) : (
                          <b className="level-test-score">{row.score_percent}%</b>
                        )}
                      </td>
                      <td className="is-center">
                        <ReportCell
                          sessionId={row.session_id}
                          reportUrl={row.report_url}
                          isCompleted={row.status === "completed"}
                          state={reportBySession[row.session_id]}
                          onBuild={handleReportBuild}
                        />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          <h4 className="level-test-subtitle">
            응시코드
            <span className="level-test-subtitle__count">{data.codes.length}개</span>
            <span className="level-test-subtitle__note">
              우리 학원에 발급된 코드입니다. 신규생을 등록해 두면 응시 안내 알림톡을
              보낼 수 있습니다.
            </span>
          </h4>
          <div className="level-test-card">
            <div className="level-test-scroll">
              <table className="level-test-table">
                <colgroup>
                  <col style={{ width: "13%" }} />
                  <col style={{ width: "23%" }} />
                  <col style={{ width: "14%" }} />
                  <col style={{ width: "10%" }} />
                  <col style={{ width: "12%" }} />
                  <col style={{ width: "9%" }} />
                  <col style={{ width: "9%" }} />
                  <col style={{ width: "10%" }} />
                </colgroup>
                <tbody>
                  <tr className="level-test-table__head">
                    <td>응시코드</td>
                    <td>받는 학생</td>
                    <td className="is-center">알림톡</td>
                    <td className="is-center">만료</td>
                    <td>응시자</td>
                    <td className="is-center">점수</td>
                    <td className="is-center">리포트</td>
                    <td className="is-center">응시</td>
                  </tr>
                  {data.codes.slice(0, 50).map((row) => {
                    const invite = inviteByCode[row.code] || row.invite;
                    const isOpen = openInviteCode === row.code;

                    return (
                      <React.Fragment key={row.code}>
                        <tr className={invite ? "level-test-row--invited" : ""}>
                          <td className="level-test-code">{row.code}</td>
                          <td>
                            {/* 등록해 두면 이름이 뜨고 줄 색이 바뀐다.
                                남의 학원을 볼 때는 등록을 열지 않는다. */}
                            <button
                              type="button"
                              className="level-test-invite-open"
                              onClick={() =>
                                !data.is_readonly &&
                                openInviteEditor(row.code, invite)
                              }
                            >
                              {invite ? (
                                <>
                                  <b>{invite.name}</b>
                                  <span className="is-muted">
                                    {[invite.grade_text, invite.school]
                                      .filter(Boolean)
                                      .join(" · ") || "정보 없음"}
                                  </span>
                                </>
                              ) : data.is_readonly ? (
                                <span className="is-muted">—</span>
                              ) : (
                                <span className="level-test-invite-add">
                                  + 학생 등록
                                </span>
                              )}
                            </button>
                          </td>
                          <td className="is-center">
                            {!invite || data.is_readonly ? (
                              <span className="is-muted">—</span>
                            ) : invite.send_status === "sent" ? (
                              <span className="level-test-badge">발송됨</span>
                            ) : (
                              <button
                                type="button"
                                className={`level-test-send${
                                  invite.send_status === "failed"
                                    ? " level-test-send--failed"
                                    : ""
                                }`}
                                disabled={sendingCode === row.code}
                                title={invite.send_error || ""}
                                onClick={() => handleInviteSend(row.code)}
                              >
                                {sendingCode === row.code
                                  ? "보내는 중…"
                                  : invite.send_status === "failed"
                                    ? "다시 발송"
                                    : "알림톡 발송"}
                              </button>
                            )}
                          </td>
                          <td className="is-center is-muted">{row.expires || "—"}</td>
                          <td>
                            {row.used_by || "—"}
                            {row.used_grade && (
                              <span className="is-muted"> {row.used_grade}</span>
                            )}
                          </td>
                          <td className="is-center">
                            {row.score_percent === null ? (
                              <span className="is-muted">—</span>
                            ) : (
                              <b className="level-test-score">{row.score_percent}%</b>
                            )}
                          </td>
                          <td className="is-center">
                            <ReportCell
                              sessionId={row.session_id}
                              reportUrl={row.report_url}
                              isCompleted={row.status === "completed"}
                              state={reportBySession[row.session_id]}
                              onBuild={handleReportBuild}
                            />
                          </td>
                          <td className="is-center">
                            {row.is_used ? (
                              <span className="level-test-badge">사용완료</span>
                            ) : (
                              <span className="is-muted">미사용</span>
                            )}
                          </td>
                        </tr>

                        {isOpen && (
                          <tr className="level-test-invite-row">
                            <td colSpan={8}>
                              <div className="level-test-invite">
                                <input
                                  className="level-test-invite__field"
                                  placeholder="학생 이름"
                                  value={inviteDraft.name}
                                  onChange={(event) =>
                                    setInviteDraft((previous) => ({
                                      ...previous,
                                      name: event.target.value,
                                    }))
                                  }
                                />
                                <input
                                  className="level-test-invite__field"
                                  placeholder="학부모 번호"
                                  value={inviteDraft.parent_phone}
                                  onChange={(event) =>
                                    setInviteDraft((previous) => ({
                                      ...previous,
                                      parent_phone: event.target.value,
                                    }))
                                  }
                                />
                                <input
                                  className="level-test-invite__field"
                                  placeholder="학교"
                                  value={inviteDraft.school}
                                  onChange={(event) =>
                                    setInviteDraft((previous) => ({
                                      ...previous,
                                      school: event.target.value,
                                    }))
                                  }
                                />
                                <input
                                  className="level-test-invite__field"
                                  placeholder="학년 (예: 중등 1학년)"
                                  value={inviteDraft.grade_text}
                                  onChange={(event) =>
                                    setInviteDraft((previous) => ({
                                      ...previous,
                                      grade_text: event.target.value,
                                    }))
                                  }
                                />
                                <button
                                  type="button"
                                  className="level-test-invite__save"
                                  onClick={() => handleInviteSave(row.code)}
                                >
                                  등록
                                </button>
                                <button
                                  type="button"
                                  className="level-test-invite__cancel"
                                  onClick={() => setOpenInviteCode(null)}
                                >
                                  취소
                                </button>
                              </div>
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}

      {tab === "vault" && <LevelTestCodeVault />}
      {tab === "all" && <AdminSessionTable />}
      {tab === "give" && <AdminCodeGive />}
      {tab === "missing" && <AdminMissingReports />}

      {tab === "reviews" && (
        <div className="level-test-panel">
          {reviewList.length === 0 ? (
            <p className="level-test-empty">
              아직 후기가 없습니다. 아이가 리포트에서 후기를 쓰면 여기에 모입니다.
            </p>
          ) : (
            <div className="level-test-review-list">
              {reviewList.map((review) => (
                <div className="level-test-review" key={review.review_id}>
                  <div className="level-test-review__head">
                    <b>{review.name}</b>
                    <span className="level-test-review__meta">
                      {review.grade} · {"★".repeat(Number(review.rating) || 0)}
                      {review.cats ? ` · ${review.cats}` : ""}
                    </span>
                    <button
                      type="button"
                      className={`level-test-review__publish${
                        review.is_published ? " is-on" : ""
                      }`}
                      onClick={() => toggleReviewPublish(review)}
                    >
                      {review.is_published ? "홈페이지 게시 중 · 내리기" : "홈페이지 전송"}
                    </button>
                  </div>
                  <p className="level-test-review__text">{review.text}</p>
                  {review.image_url && (
                    <a href={review.image_url} target="_blank" rel="noreferrer">
                      <img
                        className="level-test-review__photo"
                        src={review.image_url}
                        alt="공부한 흔적"
                      />
                    </a>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {tab === "words" && (
        <>
          <h4 className="level-test-subtitle">
            모르는 단어
            <span className="level-test-subtitle__note">
              제목과 단어를 따로 복사합니다. 지인에듀 단어장에 제목 한 번, 단어 한 번 붙여넣으면 됩니다.
            </span>
          </h4>

          {data.word_trials.length === 0 && (
            <p className="level-test-empty">아직 신청된 단어가 없습니다.</p>
          )}

          {data.word_trials.map((row, index) => (
            <div key={`${row.name}-${index}`} className="level-test-card word-trial">
              <div className="word-trial__head">
                <button
                  type="button"
                  className="word-trial__toggle"
                  onClick={() =>
                    setOpenWordRow(openWordRow === index ? null : index)
                  }
                >
                  <span className="is-strong">{row.title}</span>
                  <span className="word-trial__meta">
                    {row.grade} · {row.school || "학교 미기재"}
                  </span>
                  <span className="word-trial__count">{row.word_count}개</span>
                  <span className="word-trial__caret">
                    {openWordRow === index ? "▲" : "▼"}
                  </span>
                </button>
                <CopyButton text={row.title} label="제목 복사" />
                <CopyButton text={row.words_text} label="단어 복사" />
              </div>
              {openWordRow === index && (
                <textarea
                  className="word-trial__words"
                  readOnly
                  value={row.words_text}
                />
              )}
            </div>
          ))}
        </>
      )}
    </div>
  );
};

export default LevelTestManagement;
